// src/features/staff/pages/StaffPage.tsx
// FIXED - Consistent Data Handling + Cache Invalidation + POS Access

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import * as staffApi from '../services/staffApi';
import * as businessApi from '@/features/business/services/businessApi';
import * as settingsApi from '@/features/settings/services/settingsApi';
import type { StaffMember } from '../services/staffApi';
import type { Business } from '@/features/business/services/businessApi';
import {
    Users, Ban, CheckCircle2, ArrowLeft,
    UserPlus, UserCheck, UserX, Shield, Clock,
    Eye, Search, X, KeyRound, Building2, Store, TrendingUp,
    LayoutGrid, List, ArrowUpDown,
    Pencil, Trash2, Phone, Hash,
    AtSign, RefreshCw, Calendar, Rocket, PartyPopper, Sparkles,
    Mail, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ShoppingCart, Utensils, Layers, Save,
    User, Check, ArrowRight, Lock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// FORMATTERS
// ============================================================

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const then = new Date(date);
        const diffMs = now.getTime() - then.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(date);
    } catch { return 'N/A'; }
};

// ============================================================
// HELPERS
// ============================================================

const extractErrorMessage = (err: any): string => {
    const apiError = err?.response?.data?.error;
    if (!apiError) return err?.message || 'An unexpected error occurred.';
    if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        return apiError.details.map((d: any) => d.message).join('. ');
    }
    return apiError.message || 'An unexpected error occurred.';
};

const extractFieldErrors = (err: any): Record<string, string> => {
    const apiError = err?.response?.data?.error;
    if (!apiError?.details || !Array.isArray(apiError.details)) return {};
    const fieldErrors: Record<string, string> = {};
    apiError.details.forEach((detail: any) => {
        if (detail.field && detail.message) fieldErrors[detail.field] = detail.message;
    });
    return fieldErrors;
};

// ============================================================
// CELEBRATION OVERLAY
// ============================================================

function CelebrationOverlay({ show, staffName, onComplete }: { show: boolean; staffName: string; onComplete: () => void }) {
    useEffect(() => {
        if (show) { const timer = setTimeout(onComplete, 3000); return () => clearTimeout(timer); }
    }, [show, onComplete]);
    if (!show) return null;
    const confettiColors = ['#10B981', '#06B6D4', '#8B5CF6', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444'];
    const pieces = Array.from({ length: 50 }, (_, i) => ({
        id: i, left: `${Math.random() * 100}%`, delay: Math.random() * 2,
        duration: 1.5 + Math.random() * 2, color: confettiColors[Math.floor(Math.random() * confettiColors.length)], size: 8 + Math.random() * 12,
    }));
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
                {pieces.map(piece => (
                    <motion.div key={piece.id} initial={{ y: -20, opacity: 1 }}
                        animate={{ y: '100vh', x: `${(Math.random() - 0.5) * 200}px`, opacity: 0, rotate: 360 }}
                        transition={{ duration: piece.duration, delay: piece.delay, ease: 'easeIn' }}
                        className="absolute top-0 rounded-sm" style={{ left: piece.left, width: piece.size, height: piece.size * 0.6, backgroundColor: piece.color }} />
                ))}
                <motion.div initial={{ scale: 0, y: -50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0, y: -50 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-white rounded-3xl shadow-2xl p-8 text-center border-2 border-emerald-200">
                        <motion.div animate={{ rotate: [0, -10, 10, -10, 0] }} transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1 }}
                            className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-200">
                            <PartyPopper size={40} className="text-white" />
                        </motion.div>
                        <h2 className="text-2xl font-black text-slate-900 mb-1">🎉 Staff Added!</h2>
                        <p className="text-slate-500 font-medium"><strong className="text-emerald-600">{staffName}</strong> is now part of the team!</p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ============================================================
// STAT CARD
// ============================================================

function StatCard({ label, value, icon: Icon, gradient }: { label: string; value: number | string; icon: React.ElementType; gradient: string }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${gradient} shadow-md w-fit mb-3`}><Icon className="w-4 h-4 text-white" /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold text-slate-900 mt-0.5">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        </motion.div>
    );
}

// ============================================================
// PAGINATION
// ============================================================

const PAGE_SIZE_OPTIONS = [12, 24, 48];

function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }: {
    currentPage: number; totalPages: number; totalItems: number; pageSize: number;
    onPageChange: (page: number) => void; onPageSizeChange: (size: number) => void;
}) {
    if (totalItems === 0) return null;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    const pageNumbers: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pageNumbers.push(i); }
    else {
        pageNumbers.push(1);
        if (currentPage > 3) pageNumbers.push('ellipsis');
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pageNumbers.push(i);
        if (currentPage < totalPages - 2) pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages);
    }
    return (
        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <p className="text-sm text-slate-600">Showing <span className="font-semibold text-slate-900">{startItem}</span> to <span className="font-semibold text-slate-900">{endItem}</span> of <span className="font-semibold text-slate-900">{totalItems.toLocaleString()}</span></p>
                <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} className="px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white text-slate-600">
                    {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size} per page</option>)}
                </select>
            </div>
            <div className="flex gap-2 flex-wrap">
                <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button"><ChevronsLeft className="w-4 h-4" /></button>
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button"><ChevronLeft className="w-4 h-4" /> Prev</button>
                {pageNumbers.map((page, idx) => (
                    <React.Fragment key={idx}>
                        {page === 'ellipsis' ? <span className="px-2 py-2 text-slate-400">...</span> : (
                            <button onClick={() => onPageChange(page as number)}
                                className={clsx('px-4 py-2 rounded-xl text-sm font-medium transition', currentPage === page ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25' : 'border border-slate-200 hover:bg-slate-100 text-slate-700')} type="button">{page}</button>
                        )}
                    </React.Fragment>
                ))}
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button">Next <ChevronRight className="w-4 h-4" /></button>
                <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button"><ChevronsRight className="w-4 h-4" /></button>
            </div>
        </div>
    );
}

// ============================================================
// SKELETON
// ============================================================

function PageSkeleton() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                <div className="rounded-3xl bg-slate-200 animate-pulse h-40" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => (<div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse h-28" />))}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// NORMALIZE STAFF DATA HELPER
// ============================================================

function normalizeStaffData(raw: any): StaffMember[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    if (raw?.users && Array.isArray(raw.users)) return raw.users;
    if (raw?.data?.data && Array.isArray(raw.data.data)) return raw.data.data;
    return [];
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function StaffPage() {
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterBusiness, setFilterBusiness] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('active');
    const [filterRole, setFilterRole] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);

    const [showForm, setShowForm] = useState(false);
    const [formStep, setFormStep] = useState<1 | 2 | 3>(1);
    const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
    const [form, setForm] = useState({
        firstName: '', lastName: '', email: '', phone: '', password: '',
        employeeId: '', roleIds: [] as string[], branchIds: [] as string[],
        isActive: true,
        posAccess: 'none' as 'none' | 'retail' | 'bom' | 'both',
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [showCelebration, setShowCelebration] = useState(false);
    const [newStaffName, setNewStaffName] = useState('');
    const [confirmAction, setConfirmAction] = useState<{ type: string; staff: StaffMember } | null>(null);

    // Super Admin Reset PIN modal state
    const [pinModal, setPinModal] = useState<{
        open: boolean;
        staff: StaffMember | null;
        pin: string;
        confirmPin: string;
        showPin: boolean;
        error: string;
        isSubmitting: boolean;
    }>({
        open: false,
        staff: null,
        pin: '',
        confirmPin: '',
        showPin: false,
        error: '',
        isSubmitting: false,
    });

    // Super Admin Manage Roles modal state
    const [roleModal, setRoleModal] = useState<{
        open: boolean;
        staff: StaffMember | null;
        roleIds: string[];
        isSubmitting: boolean;
    }>({
        open: false,
        staff: null,
        roleIds: [],
        isSubmitting: false,
    });

    useEffect(() => { const timer = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(timer); }, [search]);
    useEffect(() => { queryClient.invalidateQueries({ queryKey: ['staff'] }); }, []);

    const { data: staffData, isLoading, isError, refetch } = useQuery({
        queryKey: ['staff', tenantId, debouncedSearch, sortBy, order],
        queryFn: () => staffApi.getStaff(tenantId!, { limit: 200, search: debouncedSearch || undefined, sortBy, order }),
        enabled: !!tenantId,
        staleTime: 0,
    });

    const { data: businesses } = useQuery<Business[]>({
        queryKey: ['businesses-active', tenantId],
        queryFn: businessApi.getBusinessesWithStats,
        enabled: !!tenantId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: branches } = useQuery<any[]>({
        queryKey: ['branches', tenantId],
        queryFn: async () => { const r = await settingsApi.getBranches(tenantId!); return Array.isArray(r) ? r : (r as any)?.data || []; },
        enabled: !!tenantId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: rolesData } = useQuery({
        queryKey: ['roles', tenantId],
        queryFn: () => staffApi.getRoles(tenantId!),
        enabled: !!tenantId,
        staleTime: 5 * 60 * 1000,
    });

    const createStaffMutation = useMutation({
        mutationFn: (payload: typeof form) => {
            const p: any = {
                firstName: payload.firstName.trim(), lastName: payload.lastName.trim(),
                email: payload.email.trim().toLowerCase(), password: payload.password,
                roleIds: payload.roleIds, branchIds: payload.branchIds,
                posAccess: payload.posAccess,
            };
            if (payload.phone.trim()) p.phone = payload.phone.trim();
            if (payload.employeeId.trim()) p.employeeId = payload.employeeId.trim();
            return staffApi.createStaff(tenantId!, p);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['staff'] });
            const name = `${form.firstName} ${form.lastName}`;
            addToast({ type: 'success', title: 'Staff Created! 🎉', message: `${name} has been added.`, action: { label: 'View Profile', onClick: () => navigate(`/staff/${(data as any).id}`) } });
            setNewStaffName(name); setShowCelebration(true); resetForm();
        },
        onError: (err: any) => {
            const fe = extractFieldErrors(err);
            if (Object.keys(fe).length > 0) setFormErrors(fe);
            else { const m = extractErrorMessage(err).toLowerCase(); if (m.includes('email')) setFormErrors({ email: extractErrorMessage(err) }); else if (m.includes('password')) setFormErrors({ password: extractErrorMessage(err) }); else if (m.includes('role')) setFormErrors({ roles: extractErrorMessage(err) }); }
            addToast({ type: 'error', title: 'Creation Failed', message: extractErrorMessage(err) });
        },
    });

    const updateStaffMutation = useMutation({
        mutationFn: ({ staffId, data }: { staffId: string; data: any }) => staffApi.updateStaff(tenantId!, staffId, data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['staff'] }); addToast({ type: 'success', title: 'Staff Updated ✨', message: `${form.firstName} ${form.lastName} updated.` }); resetForm(); },
        onError: (err: any) => { const fe = extractFieldErrors(err); if (Object.keys(fe).length > 0) setFormErrors(fe); addToast({ type: 'error', title: 'Update Failed', message: extractErrorMessage(err) }); },
    });

    const toggleStatusMutation = useMutation({
        mutationFn: ({ staffId, isActive }: { staffId: string; isActive: boolean }) => isActive ? staffApi.reactivateStaff(tenantId!, staffId) : staffApi.deactivateStaff(tenantId!, staffId),
        onSuccess: (_, v) => { queryClient.invalidateQueries({ queryKey: ['staff'] }); addToast({ type: 'success', title: v.isActive ? 'Activated ✅' : 'Deactivated', message: v.isActive ? 'Can now access system.' : 'Has been deactivated.' }); setConfirmAction(null); },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: extractErrorMessage(err) }),
    });

    const deleteStaffMutation = useMutation({
        mutationFn: (staffId: string) => staffApi.deactivateStaff(tenantId!, staffId),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['staff'] }); addToast({ type: 'success', title: 'Removed', message: 'Staff deactivated.' }); setConfirmAction(null); },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: extractErrorMessage(err) }),
    });

    const allStaffRaw: StaffMember[] = useMemo(() => normalizeStaffData(staffData), [staffData]);
    const totalAllStaff = allStaffRaw.length;
    const activeStaffAll = allStaffRaw.filter(s => s.isActive).length;
    const inactiveStaffAll = allStaffRaw.filter(s => !s.isActive).length;
    const withPinAll = allStaffRaw.filter(s => !!s.posPin).length;

    const staffList: StaffMember[] = useMemo(() => {
        let filtered = [...allStaffRaw];

        // Status filter
        if (filterStatus === 'active') filtered = filtered.filter(s => s.isActive === true);
        else if (filterStatus === 'inactive') filtered = filtered.filter(s => s.isActive === false);

        // Business filter (checks primary business or any assigned branch of that business)
        if (filterBusiness) {
            filtered = filtered.filter(s =>
                s.businessId === filterBusiness ||
                (s as any).branches?.some((b: any) => b.businessId === filterBusiness)
            );
        }

        // Role filter
        if (filterRole) {
            filtered = filtered.filter(s => s.roles?.some(r => r.id === filterRole));
        }

        // Search filter (name, email, phone, employeeId, role, business, branch)
        if (debouncedSearch) {
            const term = debouncedSearch.toLowerCase();
            filtered = filtered.filter(s =>
                s.firstName?.toLowerCase().includes(term) ||
                s.lastName?.toLowerCase().includes(term) ||
                s.email?.toLowerCase().includes(term) ||
                (s.employeeId || '').toLowerCase().includes(term) ||
                (s.phone || '').toLowerCase().includes(term) ||
                s.roles?.some(r => r.name.toLowerCase().includes(term)) ||
                (s.businessName || '').toLowerCase().includes(term) ||
                (s.branchName || '').toLowerCase().includes(term) ||
                (s as any).branches?.some((b: any) => b.name?.toLowerCase().includes(term))
            );
        }

        filtered.sort((a, b) => {
            let aV: any = (a as any)[sortBy], bV: any = (b as any)[sortBy];
            if (sortBy === 'firstName' || sortBy === 'lastName' || sortBy === 'email') {
                aV = String(aV || '').toLowerCase();
                bV = String(bV || '').toLowerCase();
                return order === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
            }
            if (sortBy === 'createdAt') {
                aV = new Date(aV || 0).getTime();
                bV = new Date(bV || 0).getTime();
                return order === 'asc' ? aV - bV : bV - aV;
            }
            return 0;
        });
        return filtered;
    }, [allStaffRaw, filterStatus, filterBusiness, filterRole, debouncedSearch, sortBy, order]);

    const totalCount = staffList.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const paginatedStaff = staffList.slice((page - 1) * pageSize, page * pageSize);
    const activeStaff = staffList.filter(s => s.isActive).length;
    const inactiveStaff = staffList.filter(s => !s.isActive).length;
    const withPin = staffList.filter(s => s.posPin).length;

    // Reset PIN Handler
    const handleResetPinSubmit = async () => {
        if (!pinModal.staff || !tenantId) return;
        if (!/^[0-9]{4,6}$/.test(pinModal.pin)) {
            setPinModal(p => ({ ...p, error: 'PIN must be between 4 and 6 numeric digits.' }));
            return;
        }
        if (pinModal.pin !== pinModal.confirmPin) {
            setPinModal(p => ({ ...p, error: 'PIN and confirmation do not match.' }));
            return;
        }
        try {
            setPinModal(p => ({ ...p, isSubmitting: true, error: '' }));
            await staffApi.setStaffPin(tenantId, pinModal.staff.id, pinModal.pin);
            queryClient.invalidateQueries({ queryKey: ['staff'] });
            addToast({
                type: 'success',
                title: 'PIN Updated 🔑',
                message: `Security PIN updated for ${pinModal.staff.firstName} ${pinModal.staff.lastName}.`,
            });
            setPinModal({ open: false, staff: null, pin: '', confirmPin: '', showPin: false, error: '', isSubmitting: false });
        } catch (err: any) {
            setPinModal(p => ({ ...p, isSubmitting: false, error: extractErrorMessage(err) }));
            addToast({ type: 'error', title: 'Failed to update PIN', message: extractErrorMessage(err) });
        }
    };

    // Save Roles Handler
    const handleSaveRoles = async () => {
        if (!roleModal.staff || !tenantId) return;
        if (roleModal.roleIds.length === 0) {
            addToast({ type: 'warning', title: 'Role Required', message: 'Staff member must have at least one assigned role.' });
            return;
        }
        try {
            setRoleModal(p => ({ ...p, isSubmitting: true }));
            await staffApi.updateStaff(tenantId, roleModal.staff.id, {
                roleIds: roleModal.roleIds,
            });
            queryClient.invalidateQueries({ queryKey: ['staff'] });
            addToast({
                type: 'success',
                title: 'Roles Updated ✨',
                message: `Assigned roles updated for ${roleModal.staff.firstName}.`,
            });
            setRoleModal({ open: false, staff: null, roleIds: [], isSubmitting: false });
        } catch (err: any) {
            setRoleModal(p => ({ ...p, isSubmitting: false }));
            addToast({ type: 'error', title: 'Role Update Failed', message: extractErrorMessage(err) });
        }
    };

    const updateFormField = (field: string, value: any) => {
        setForm(p => ({ ...p, [field]: value }));
        if (formErrors[field]) setFormErrors(p => { const u = { ...p }; delete u[field]; return u; });
    };

    const resetForm = useCallback(() => {
        setForm({
            firstName: '', lastName: '', email: '', phone: '', password: '',
            employeeId: '', roleIds: [], branchIds: [], isActive: true, posAccess: 'none'
        });
        setFormErrors({});
        setShowForm(false);
        setEditingStaff(null);
        setFormStep(1);
    }, []);

    const openCreateForm = useCallback(() => {
        setEditingStaff(null);
        resetForm();
        setFormStep(1);
        setShowForm(true);
    }, [resetForm]);

    const openEditForm = useCallback((staff: StaffMember) => {
        setEditingStaff(staff);
        const bids: string[] = [];
        if ((staff as any).branchId) bids.push((staff as any).branchId);
        if ((staff as any).branches) (staff as any).branches.forEach((b: any) => { if (b.branchId) bids.push(b.branchId); else if (b.id) bids.push(b.id); });
        if ((staff as any).staffBranches) (staff as any).staffBranches.forEach((b: any) => { if (b.branchId) bids.push(b.branchId); else if (b.id) bids.push(b.id); });
        setForm({
            firstName: staff.firstName, lastName: staff.lastName, email: staff.email,
            phone: staff.phone || '', password: '', employeeId: staff.employeeId || '',
            roleIds: staff.roles.map(r => r.id), branchIds: bids, isActive: staff.isActive,
            posAccess: staff.posAccess || 'none',
        });
        setFormErrors({});
        setFormStep(1);
        setShowForm(true);
    }, []);

    const validateStep1 = useCallback(() => {
        const e: Record<string, string> = {};
        if (!form.firstName.trim()) e.firstName = 'First name is required';
        if (!form.lastName.trim()) e.lastName = 'Last name is required';
        if (!form.email.trim()) e.email = 'Email address is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Invalid email address';

        if (!editingStaff) {
            if (!form.password) e.password = 'Password is required';
            else if (form.password.length < 8) e.password = 'Min 8 chars';
            else if (!/[A-Z]/.test(form.password)) e.password = 'Need uppercase letter';
            else if (!/[a-z]/.test(form.password)) e.password = 'Need lowercase letter';
            else if (!/[0-9]/.test(form.password)) e.password = 'Need number';
            else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(form.password)) e.password = 'Need special symbol';
        }

        setFormErrors(prev => {
            const next = { ...prev };
            delete next.firstName; delete next.lastName; delete next.email; delete next.password;
            return { ...next, ...e };
        });

        if (Object.keys(e).length > 0) {
            addToast({ type: 'warning', title: 'Incomplete Details', message: 'Please fix the highlighted fields before proceeding.' });
            return false;
        }
        return true;
    }, [form, editingStaff, addToast]);

    const validateStep2 = useCallback(() => {
        const e: Record<string, string> = {};
        if (form.roleIds.length === 0) e.roles = 'Please assign at least one role';

        setFormErrors(prev => {
            const next = { ...prev };
            delete next.roles;
            return { ...next, ...e };
        });

        if (Object.keys(e).length > 0) {
            addToast({ type: 'warning', title: 'Role Required', message: 'Please select at least one role for this staff member.' });
            return false;
        }
        return true;
    }, [form.roleIds, addToast]);

    const handleNextStep = useCallback(() => {
        if (formStep === 1) {
            if (validateStep1()) setFormStep(2);
        } else if (formStep === 2) {
            if (validateStep2()) setFormStep(3);
        }
    }, [formStep, validateStep1, validateStep2]);

    const handleSubmit = useCallback(() => {
        if (!validateStep1()) { setFormStep(1); return; }
        if (!validateStep2()) { setFormStep(2); return; }
        if (editingStaff) {
            updateStaffMutation.mutate({
                staffId: editingStaff.id,
                data: {
                    firstName: form.firstName, lastName: form.lastName,
                    phone: form.phone || null, employeeId: form.employeeId || null,
                    roleIds: form.roleIds, branchIds: form.branchIds, isActive: form.isActive,
                    posAccess: form.posAccess,
                }
            });
        } else {
            createStaffMutation.mutate(form);
        }
    }, [form, editingStaff, validateStep1, validateStep2, updateStaffMutation, createStaffMutation]);

    const handleBranchToggle = useCallback((bid: string) => { setForm(p => ({ ...p, branchIds: p.branchIds.includes(bid) ? p.branchIds.filter(id => id !== bid) : [...p.branchIds, bid] })); }, []);
    const handleSelectAllBranches = useCallback((bid: string) => {
        const bb = (branches || []).filter((b: any) => b.businessId === bid);
        const nids = bb.map((b: any) => b.id);
        setForm(p => { const all = nids.every(id => p.branchIds.includes(id)); if (all) return { ...p, branchIds: p.branchIds.filter(id => !nids.includes(id)) }; return { ...p, branchIds: [...new Set([...p.branchIds, ...nids])] }; });
    }, [branches]);

    const clearFilters = useCallback(() => { setFilterStatus('active'); setFilterBusiness(''); setFilterRole(''); setSearch(''); setDebouncedSearch(''); setPage(1); }, []);

    const getStaffBranchNames = (staff: any): string => {
        const bids: string[] = [];
        if (staff.branchId) bids.push(staff.branchId);
        if (staff.branches) staff.branches.forEach((b: any) => { if (b.branchId) bids.push(b.branchId); else if (b.id) bids.push(b.id); });
        if (staff.staffBranches) staff.staffBranches.forEach((b: any) => { if (b.branchId) bids.push(b.branchId); else if (b.id) bids.push(b.id); });
        if (bids.length === 0) return '';
        return bids.map(id => (branches || []).find((b: any) => b.id === id)?.name).filter(Boolean).join(', ');
    };

    const handlePageChange = (p: number) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const handlePageSizeChange = (s: number) => { setPageSize(s); setPage(1); };

    if (isLoading) return <PageSkeleton />;
    if (isError) return <div className="min-h-screen flex items-center justify-center p-4"><ErrorState title="Failed to load staff" onRetry={() => refetch()} /></div>;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                <CelebrationOverlay show={showCelebration} staffName={newStaffName} onComplete={() => setShowCelebration(false)} />

                {/* HEADER */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-600 mb-3 group"><ArrowLeft size={16} className="group-hover:-translate-x-1" />Back to Dashboard</button>
                    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 rounded-3xl shadow-2xl">
                        <div className="absolute inset-0"><div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" /><div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" /></div>
                        <div className="relative px-6 lg:px-10 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div whileHover={{ rotate: -5, scale: 1.05 }} className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center shadow-xl"><Users size={32} className="text-white" /></motion.div>
                                <div><h1 className="text-3xl lg:text-4xl font-black text-white">Staff Management</h1><p className="text-teal-100 text-sm mt-1 font-medium">{totalCount} members · {activeStaff} active · {inactiveStaff} inactive</p></div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Button onClick={() => navigate('/staff/performance')} size="sm" className="!bg-white/10 !text-white !border-white/20 hover:!bg-white/20" leftIcon={<TrendingUp size={14} />}>Performance</Button>
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}><Button size="md" className="!bg-white !text-emerald-700 hover:!text-emerald-800 !font-bold !px-6 !py-3 !rounded-2xl !text-base !border-2 !border-white/80 !shadow-xl !shadow-black/20" leftIcon={<UserPlus size={18} />} onClick={openCreateForm}>Add Staff</Button></motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* STATS */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label="Total Staff" value={totalAllStaff} icon={Users} gradient="from-emerald-500 to-teal-600" />
                    <StatCard label="Active" value={activeStaffAll} icon={UserCheck} gradient="from-green-500 to-emerald-600" />
                    <StatCard label="Inactive" value={inactiveStaffAll} icon={UserX} gradient="from-slate-400 to-slate-500" />
                    <StatCard label="PIN Set" value={withPinAll} icon={KeyRound} gradient="from-amber-500 to-orange-600" />
                </div>

                {/* TOOLBAR */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm sticky top-4 z-30 backdrop-blur-xl bg-white/95 p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><input type="text" placeholder="Search staff, role, branch..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-8 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs w-48 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />{search && <button onClick={() => { setSearch(''); setDebouncedSearch(''); }} className="absolute right-2 top-1/2 -translate-y-1/2"><X size={12} className="text-slate-400" /></button>}</div>
                        <select value={filterBusiness} onChange={e => { setFilterBusiness(e.target.value); setPage(1); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600"><option value="">All Businesses</option>{(businesses || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
                        <select value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600"><option value="">All Roles</option>{(rolesData || []).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
                        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                            {[{ value: 'active', label: `Active (${activeStaffAll})` }, { value: 'inactive', label: `Inactive (${inactiveStaffAll})` }, { value: '', label: `All (${totalAllStaff})` }].map(s => <button key={s.value} onClick={() => { setFilterStatus(s.value); setPage(1); }} className={clsx('px-3 py-1.5 rounded-md text-[10px] font-bold transition-all', filterStatus === s.value ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-500 hover:text-slate-700')}>{s.label}</button>)}
                        </div>
                        {(filterStatus !== 'active' || filterBusiness || filterRole || search) && <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1 text-[10px] text-rose-600 hover:bg-rose-50 rounded-lg font-medium"><X size={12} />Clear</button>}
                    </div>
                    <div className="flex items-center gap-2">
                        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600"><option value="createdAt">Date</option><option value="firstName">First Name</option><option value="lastName">Last Name</option><option value="email">Email</option></select>
                        <button onClick={() => setOrder(o => o === 'asc' ? 'desc' : 'asc')} className={clsx('p-2 rounded-lg', order === 'asc' ? 'text-emerald-600' : 'text-slate-400')}><ArrowUpDown size={14} /></button>
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                            <button onClick={() => setViewMode('table')} className={clsx('p-2 rounded-md', viewMode === 'table' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400')}><List size={16} /></button>
                            <button onClick={() => setViewMode('card')} className={clsx('p-2 rounded-md', viewMode === 'card' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400')}><LayoutGrid size={16} /></button>
                        </div>
                        <button onClick={() => refetch()} className="p-2 rounded-lg text-slate-400" title="Refresh"><RefreshCw size={14} /></button>
                    </div>
                </div>

                {/* STAFF LIST */}
                {staffList.length === 0 ? (
                    <EmptyState icon={<Users size={48} />} title="No staff" description="Add your first team member." action={{ label: 'Add Staff', onClick: openCreateForm }} />
                ) : viewMode === 'table' ? (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead><tr className="bg-slate-50"><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase">Staff</th><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase hidden md:table-cell">Contact</th><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase hidden lg:table-cell">Business / Branch</th><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase hidden xl:table-cell">Roles</th><th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase">POS</th><th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase">PIN</th><th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase">Status</th><th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase hidden 2xl:table-cell">Last Login</th><th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase">Actions</th></tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedStaff.map(s => {
                                        const bn = getStaffBranchNames(s);
                                        const posAccessLabel = s.posAccess === 'both' ? 'Both' : s.posAccess === 'retail' ? 'Retail' : s.posAccess === 'bom' ? 'Menu' : 'None';
                                        return (
                                            <tr key={s.id} className="hover:bg-slate-50/50 group transition-colors">
                                                <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/staff/${s.id}`)}><div className="flex items-center gap-3"><div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-md', s.isActive ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-slate-400 to-slate-500')}>{s.firstName?.[0]}{s.lastName?.[0]}</div><div className="min-w-0"><p className="text-sm font-bold text-slate-900 truncate group-hover:text-emerald-600">{s.firstName} {s.lastName}</p>{s.employeeId && <span className="text-[10px] text-slate-400 font-mono">{s.employeeId}</span>}</div></div></td>
                                                <td className="px-6 py-4 hidden md:table-cell"><div className="space-y-0.5"><div className="text-xs text-slate-600 flex items-center gap-1"><Mail size={10} className="text-slate-400" /><span className="truncate max-w-[180px]">{s.email}</span></div>{s.phone && <div className="text-[10px] text-slate-400 flex items-center gap-1"><Phone size={10} />{s.phone}</div>}</div></td>
                                                <td className="px-6 py-4 hidden lg:table-cell"><div className="space-y-0.5">{s.businessName && <span className="text-xs text-emerald-600 flex items-center gap-1"><Building2 size={10} />{s.businessName}</span>}{bn && <span className="text-[10px] text-slate-400 flex items-center gap-1"><Store size={9} />{bn}</span>}{!s.businessName && !bn && <span className="text-[10px] text-slate-400 italic">Not assigned</span>}</div></td>
                                                <td className="px-6 py-4 hidden xl:table-cell"><div className="flex gap-1 flex-wrap">{s.roles?.length > 0 ? <>{s.roles.slice(0, 2).map(r => <Badge key={r.id} variant="neutral" size="sm">{r.name}</Badge>)}{s.roles.length > 2 && <Badge variant="neutral" size="sm">+{s.roles.length - 2}</Badge>}</> : <span className="text-[10px] text-slate-400 italic">No roles</span>}</div></td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge variant={posAccessLabel === 'Both' ? 'success' : posAccessLabel === 'None' ? 'neutral' : 'info'} size="sm">
                                                        {posAccessLabel === 'Both' ? <Layers size={9} className="mr-0.5" /> : posAccessLabel === 'Retail' ? <ShoppingCart size={9} className="mr-0.5" /> : posAccessLabel === 'Menu' ? <Utensils size={9} className="mr-0.5" /> : null}
                                                        {posAccessLabel}
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 text-center">{s.posPin ? <Badge variant="success" size="sm"><KeyRound size={9} className="mr-0.5" />Set</Badge> : <Badge variant="warning" size="sm">Not Set</Badge>}</td>
                                                <td className="px-6 py-4 text-center"><Badge variant={s.isActive ? 'success' : 'danger'} size="sm">{s.isActive ? 'Active' : 'Inactive'}</Badge></td>
                                                <td className="px-6 py-4 hidden 2xl:table-cell">{s.lastLoginAt ? <div className="text-xs"><p className="text-slate-500">{formatTimeAgo(s.lastLoginAt)}</p></div> : <span className="text-xs text-slate-400 italic">Never</span>}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                        <button onClick={() => navigate(`/staff/${s.id}`)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600" title="View Profile"><Eye size={14} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); openEditForm(s); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600" title="Edit Staff"><Pencil size={14} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); setRoleModal({ open: true, staff: s, roleIds: s.roles.map(r => r.id), isSubmitting: false }); }} className="p-1.5 rounded-lg hover:bg-purple-50 text-slate-400 hover:text-purple-600" title="Manage Roles"><Shield size={14} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); setPinModal({ open: true, staff: s, pin: '', confirmPin: '', showPin: false, error: '', isSubmitting: false }); }} className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600" title="Reset PIN"><KeyRound size={14} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: s.isActive ? 'deactivate' : 'activate', staff: s }); }} className={clsx('p-1.5 rounded-lg', s.isActive ? 'hover:bg-amber-50 text-slate-400 hover:text-amber-600' : 'hover:bg-emerald-50 text-slate-400 hover:text-emerald-600')} title={s.isActive ? 'Deactivate' : 'Activate'}>{s.isActive ? <Ban size={14} /> : <CheckCircle2 size={14} />}</button>
                                                        <button onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'delete', staff: s }); }} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600" title="Remove"><Trash2 size={14} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination currentPage={page} totalPages={totalPages} totalItems={totalCount} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
                    </div>
                ) : (
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {paginatedStaff.map((s, i) => {
                                const bn = getStaffBranchNames(s);
                                const posAccessLabel = s.posAccess === 'both' ? 'Both POS' : s.posAccess === 'retail' ? 'Retail POS' : s.posAccess === 'bom' ? 'Menu POS' : 'No POS';
                                return (
                                    <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} whileHover={{ y: -4 }} className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:shadow-xl hover:border-emerald-300 transition-all group overflow-hidden flex flex-col">
                                        <div className={`h-1.5 w-full ${s.isActive ? 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500' : 'bg-gradient-to-r from-slate-300 via-slate-400 to-slate-500'}`} />
                                        <div className="p-5 flex flex-col flex-1">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className={clsx('w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-lg cursor-pointer transition-transform group-hover:scale-105', s.isActive ? 'bg-gradient-to-br from-emerald-400 to-teal-600 ring-2 ring-emerald-100' : 'bg-gradient-to-br from-slate-400 to-slate-600 ring-2 ring-slate-100')} onClick={() => navigate(`/staff/${s.id}`)}>{s.firstName?.[0]}{s.lastName?.[0]}</div>
                                                <div className="flex flex-col items-end gap-1.5"><Badge variant={s.isActive ? 'success' : 'danger'} size="sm">{s.isActive ? 'Active' : 'Inactive'}</Badge>{s.posPin ? <Badge variant="info" size="sm"><KeyRound size={9} className="mr-0.5" />PIN</Badge> : <Badge variant="warning" size="sm">No PIN</Badge>}</div>
                                            </div>
                                            <h3 className="font-bold text-slate-900 text-base cursor-pointer hover:text-emerald-600 truncate mb-3" onClick={() => navigate(`/staff/${s.id}`)}>{s.firstName} {s.lastName}</h3>
                                            <div className="space-y-2.5 mb-4 flex-1">
                                                <div className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 rounded-xl"><div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center"><Mail size={13} className="text-blue-500" /></div><span className="text-xs text-slate-600 truncate">{s.email}</span></div>
                                                {s.phone && <div className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 rounded-xl"><div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center"><Phone size={13} className="text-green-500" /></div><span className="text-xs text-slate-600">{s.phone}</span></div>}
                                                {s.businessName && <div className="flex items-center gap-2.5 px-3 py-2 bg-emerald-50 rounded-xl"><div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center"><Building2 size={13} className="text-emerald-600" /></div><span className="text-xs text-emerald-700 font-medium truncate">{s.businessName}</span></div>}
                                                {bn && <div className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 rounded-xl"><div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><Store size={13} className="text-amber-500" /></div><span className="text-xs text-slate-600 truncate">{bn}</span></div>}
                                                <div className="flex items-center gap-2.5 px-3 py-2 bg-purple-50 rounded-xl">
                                                    <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                                                        {s.posAccess === 'both' ? <Layers size={13} className="text-purple-600" /> : s.posAccess === 'retail' ? <ShoppingCart size={13} className="text-purple-600" /> : s.posAccess === 'bom' ? <Utensils size={13} className="text-purple-600" /> : <Ban size={13} className="text-purple-400" />}
                                                    </div>
                                                    <span className="text-xs text-purple-700 font-medium">{posAccessLabel}</span>
                                                </div>
                                            </div>
                                            <div className="mb-4">{s.roles?.length > 0 ? <div className="flex gap-1.5 flex-wrap">{s.roles.slice(0, 3).map(r => <Badge key={r.id} variant="neutral" size="sm">{r.name}</Badge>)}{s.roles.length > 3 && <Badge variant="neutral" size="sm">+{s.roles.length - 3}</Badge>}</div> : <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 rounded-xl border border-dashed border-rose-200"><Shield size={12} className="text-rose-400" /><span className="text-[11px] text-rose-500 font-medium">No roles</span></div>}</div>
                                            <div className="grid grid-cols-4 gap-1.5 pt-3 border-t border-slate-100">
                                                <button onClick={() => navigate(`/staff/${s.id}`)} className="flex items-center justify-center gap-1 text-[11px] font-medium text-emerald-600 hover:bg-emerald-50 py-1.5 rounded-lg transition-colors"><Eye size={12} />View</button>
                                                <button onClick={(e) => { e.stopPropagation(); openEditForm(s); }} className="flex items-center justify-center gap-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 py-1.5 rounded-lg transition-colors"><Pencil size={12} />Edit</button>
                                                <button onClick={(e) => { e.stopPropagation(); setPinModal({ open: true, staff: s, pin: '', confirmPin: '', showPin: false, error: '', isSubmitting: false }); }} className="flex items-center justify-center gap-1 text-[11px] font-medium text-amber-600 hover:bg-amber-50 py-1.5 rounded-lg transition-colors"><KeyRound size={12} />PIN</button>
                                                <button onClick={(e) => { e.stopPropagation(); setRoleModal({ open: true, staff: s, roleIds: s.roles.map(r => r.id), isSubmitting: false }); }} className="flex items-center justify-center gap-1 text-[11px] font-medium text-purple-600 hover:bg-purple-50 py-1.5 rounded-lg transition-colors"><Shield size={12} />Roles</button>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                        {totalPages > 1 && <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"><Pagination currentPage={page} totalPages={totalPages} totalItems={totalCount} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} /></div>}
                    </div>
                )}

                {/* CREATE/EDIT MODAL - 3-STEPPED WIZARD */}
                <AnimatePresence>
                    {showForm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={resetForm}>
                            <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col">
                                {/* MODAL HEADER & STEPPER */}
                                <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-teal-700 text-white px-7 pt-6 pb-5">
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20">
                                                {editingStaff ? <Pencil size={20} className="text-white" /> : <UserPlus size={20} className="text-white" />}
                                            </div>
                                            <div>
                                                <h2 className="text-lg font-bold text-white leading-tight">
                                                    {editingStaff ? `Edit ${form.firstName || 'Staff'}` : 'Add New Staff Member'}
                                                </h2>
                                                <p className="text-teal-100 text-xs mt-0.5">
                                                    Step {formStep} of 3 • {formStep === 1 ? 'Personal & Account Info' : formStep === 2 ? 'Roles & POS Access' : 'Branch Deployment & Review'}
                                                </p>
                                            </div>
                                        </div>
                                        <button onClick={resetForm} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
                                            <X size={18} />
                                        </button>
                                    </div>

                                    {/* Stepper Progress Tabs */}
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { step: 1, label: '1. Account', icon: User },
                                            { step: 2, label: '2. Permissions', icon: Shield },
                                            { step: 3, label: '3. Branches', icon: Store },
                                        ].map(item => {
                                            const isDone = formStep > item.step;
                                            const isCurrent = formStep === item.step;
                                            return (
                                                <button
                                                    key={item.step}
                                                    type="button"
                                                    onClick={() => {
                                                        if (item.step < formStep) setFormStep(item.step as 1 | 2 | 3);
                                                        else if (item.step === 2 && formStep === 1) handleNextStep();
                                                        else if (item.step === 3 && formStep === 2) handleNextStep();
                                                    }}
                                                    className={clsx(
                                                        'flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-semibold transition-all text-left',
                                                        isCurrent
                                                            ? 'bg-white text-teal-800 shadow-md font-bold'
                                                            : isDone
                                                            ? 'bg-white/20 text-white hover:bg-white/30 cursor-pointer'
                                                            : 'bg-white/10 text-white/60 cursor-default'
                                                    )}
                                                >
                                                    {isDone ? (
                                                        <Check size={14} className="text-emerald-300 shrink-0" />
                                                    ) : (
                                                        <item.icon size={13} className={isCurrent ? 'text-teal-700' : 'text-white/60'} />
                                                    )}
                                                    <span className="truncate">{item.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* MODAL BODY - ZERO SCROLL CONTENT */}
                                <div className="p-7 min-h-[340px] flex flex-col justify-between">
                                    <AnimatePresence mode="wait">
                                        {/* STEP 1: PERSONAL & ACCOUNT PROFILE */}
                                        {formStep === 1 && (
                                            <motion.div
                                                key="step1"
                                                initial={{ opacity: 0, x: -12 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: 12 }}
                                                transition={{ duration: 0.18 }}
                                                className="space-y-4"
                                            >
                                                <div className="grid grid-cols-2 gap-4">
                                                    <Input
                                                        label="First Name *"
                                                        value={form.firstName}
                                                        error={formErrors.firstName}
                                                        onChange={e => updateFormField('firstName', e.target.value)}
                                                        placeholder="e.g. John"
                                                        required
                                                    />
                                                    <Input
                                                        label="Last Name *"
                                                        value={form.lastName}
                                                        error={formErrors.lastName}
                                                        onChange={e => updateFormField('lastName', e.target.value)}
                                                        placeholder="e.g. Doe"
                                                        required
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <Input
                                                        label="Email Address *"
                                                        type="email"
                                                        value={form.email}
                                                        error={formErrors.email}
                                                        onChange={e => updateFormField('email', e.target.value)}
                                                        disabled={!!editingStaff}
                                                        placeholder="john.doe@company.com"
                                                        required
                                                    />
                                                    <Input
                                                        label="Phone Number"
                                                        value={form.phone}
                                                        error={formErrors.phone}
                                                        onChange={e => updateFormField('phone', e.target.value)}
                                                        placeholder="+234 800 000 0000"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-4 items-start">
                                                    <Input
                                                        label="Employee ID (Optional)"
                                                        value={form.employeeId}
                                                        onChange={e => updateFormField('employeeId', e.target.value)}
                                                        placeholder="EMP-001"
                                                    />
                                                    {!editingStaff ? (
                                                        <Input
                                                            label="Initial Password *"
                                                            type="password"
                                                            value={form.password}
                                                            error={formErrors.password}
                                                            onChange={e => updateFormField('password', e.target.value)}
                                                            placeholder="••••••••"
                                                            hint="Min 8 chars, 1 uppercase, 1 number, 1 symbol"
                                                            required
                                                        />
                                                    ) : (
                                                        <div>
                                                            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Account Status</label>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateFormField('isActive', true)}
                                                                    className={clsx(
                                                                        'flex-1 py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all flex items-center justify-center gap-1.5',
                                                                        form.isActive
                                                                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm'
                                                                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                                                                    )}
                                                                >
                                                                    <CheckCircle2 size={14} className={form.isActive ? 'text-emerald-600' : 'text-slate-400'} />
                                                                    Active
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateFormField('isActive', false)}
                                                                    className={clsx(
                                                                        'flex-1 py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all flex items-center justify-center gap-1.5',
                                                                        !form.isActive
                                                                            ? 'bg-rose-50 border-rose-400 text-rose-700 shadow-sm'
                                                                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                                                                    )}
                                                                >
                                                                    <Ban size={14} className={!form.isActive ? 'text-rose-500' : 'text-slate-400'} />
                                                                    Inactive
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* STEP 2: ROLES & POS ACCESS PERMISSIONS */}
                                        {formStep === 2 && (
                                            <motion.div
                                                key="step2"
                                                initial={{ opacity: 0, x: -12 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: 12 }}
                                                transition={{ duration: 0.18 }}
                                                className="space-y-4"
                                            >
                                                {/* POS Access Selector */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">POS Access Level</label>
                                                        <span className="text-[11px] text-slate-400">Controls terminal capabilities</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2.5">
                                                        {[
                                                            { value: 'none', label: 'No POS', icon: Ban, desc: 'Backoffice only' },
                                                            { value: 'retail', label: 'Retail POS', icon: ShoppingCart, desc: 'Sales & checkout' },
                                                            { value: 'bom', label: 'Menu POS', icon: Utensils, desc: 'Restaurant & recipes' },
                                                            { value: 'both', label: 'Full POS', icon: Layers, desc: 'Retail + Restaurant' },
                                                        ].map(option => {
                                                            const isSelected = form.posAccess === option.value;
                                                            return (
                                                                <button
                                                                    key={option.value}
                                                                    type="button"
                                                                    onClick={() => updateFormField('posAccess', option.value)}
                                                                    className={clsx(
                                                                        'flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left relative overflow-hidden',
                                                                        isSelected
                                                                            ? 'border-emerald-500 bg-emerald-50/80 shadow-sm'
                                                                            : 'border-slate-200 hover:border-slate-300 bg-white'
                                                                    )}
                                                                >
                                                                    <div className={clsx(
                                                                        'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors',
                                                                        isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                                                                    )}>
                                                                        <option.icon size={18} />
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <p className={clsx('text-xs font-bold', isSelected ? 'text-emerald-800' : 'text-slate-800')}>
                                                                            {option.label}
                                                                        </p>
                                                                        <p className="text-[10px] text-slate-400 truncate">{option.desc}</p>
                                                                    </div>
                                                                    {isSelected && (
                                                                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-emerald-500" />
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Role Assignment */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                                                            Assigned Roles * <span className="text-emerald-600 font-bold">({form.roleIds.length} selected)</span>
                                                        </label>
                                                        {formErrors.roles && <span className="text-xs text-rose-500 font-medium">{formErrors.roles}</span>}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-200/80 min-h-[85px] items-center content-start">
                                                        {(rolesData || []).map((r: any) => {
                                                            const isAssigned = form.roleIds.includes(r.id);
                                                            return (
                                                                <button
                                                                    key={r.id}
                                                                    type="button"
                                                                    onClick={() => updateFormField(
                                                                        'roleIds',
                                                                        isAssigned
                                                                            ? form.roleIds.filter(id => id !== r.id)
                                                                            : [...form.roleIds, r.id]
                                                                    )}
                                                                    className={clsx(
                                                                        'px-3.5 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all flex items-center gap-1.5',
                                                                        isAssigned
                                                                            ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                                                                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                                                                    )}
                                                                >
                                                                    {isAssigned && <Check size={12} className="stroke-[3]" />}
                                                                    <Shield size={12} className={isAssigned ? 'text-white' : 'text-slate-400'} />
                                                                    {r.name}
                                                                </button>
                                                            );
                                                        })}
                                                        {(!rolesData || rolesData.length === 0) && (
                                                            <p className="text-xs text-slate-400 italic">No roles configured yet.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* STEP 3: BRANCH ASSIGNMENT & REVIEW */}
                                        {formStep === 3 && (
                                            <motion.div
                                                key="step3"
                                                initial={{ opacity: 0, x: -12 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: 12 }}
                                                transition={{ duration: 0.18 }}
                                                className="space-y-3.5"
                                            >
                                                {/* Branches Selection */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                                                            Branch Deployment ({form.branchIds.length} assigned)
                                                        </label>
                                                        <span className="text-[11px] text-slate-400">Select branch access</span>
                                                    </div>

                                                    <div className="max-h-[160px] overflow-y-auto pr-1 space-y-2 border border-slate-200 rounded-2xl p-2.5 bg-slate-50/50">
                                                        {(businesses || []).map(biz => {
                                                            const bb = (branches || []).filter((b: any) => b.businessId === biz.id);
                                                            if (!bb.length) return null;
                                                            const all = bb.every((b: any) => form.branchIds.includes(b.id));
                                                            return (
                                                                <div key={biz.id} className="p-2.5 bg-white rounded-xl border border-slate-100 shadow-xs">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                                                            <Building2 size={13} className="text-teal-600" />
                                                                            {biz.name}
                                                                        </p>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSelectAllBranches(biz.id)}
                                                                            className="text-[10px] text-emerald-600 hover:text-emerald-700 font-semibold"
                                                                        >
                                                                            {all ? 'Deselect All' : 'Select All'}
                                                                        </button>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {bb.map((b: any) => {
                                                                            const isSelected = form.branchIds.includes(b.id);
                                                                            return (
                                                                                <button
                                                                                    key={b.id}
                                                                                    type="button"
                                                                                    onClick={() => handleBranchToggle(b.id)}
                                                                                    className={clsx(
                                                                                        'px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all flex items-center gap-1',
                                                                                        isSelected
                                                                                            ? 'bg-emerald-50 border-emerald-400 text-emerald-700 font-semibold shadow-xs'
                                                                                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                                                                    )}
                                                                                >
                                                                                    <Store size={10} className={isSelected ? 'text-emerald-600' : 'text-slate-400'} />
                                                                                    {b.name}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {(!businesses || businesses.length === 0) && (
                                                            <p className="text-xs text-slate-400 p-2 italic text-center">No active businesses or branches found.</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Staff Summary Review Pill Box */}
                                                <div className="p-3 bg-gradient-to-br from-emerald-50 via-teal-50 to-slate-50 border border-teal-200/80 rounded-2xl flex items-center justify-between">
                                                    <div className="min-w-0 pr-3">
                                                        <p className="text-xs font-bold text-slate-800 truncate">
                                                            {form.firstName || 'Staff'} {form.lastName}
                                                        </p>
                                                        <p className="text-[11px] text-slate-500 truncate">{form.email || 'No email provided'}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="px-2.5 py-1 bg-white rounded-lg border border-teal-200 text-[10px] font-bold text-teal-700 uppercase">
                                                            {form.posAccess === 'both' ? 'Full POS' : form.posAccess === 'retail' ? 'Retail' : form.posAccess === 'bom' ? 'Menu' : 'No POS'}
                                                        </span>
                                                        <span className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg text-[10px] font-bold">
                                                            {form.roleIds.length} {form.roleIds.length === 1 ? 'Role' : 'Roles'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* MODAL FOOTER CONTROLS */}
                                <div className="bg-slate-50 border-t border-slate-200 px-7 py-4 flex items-center justify-between">
                                    <Button variant="secondary" onClick={resetForm} size="sm">
                                        Cancel
                                    </Button>

                                    <div className="flex items-center gap-2">
                                        {formStep > 1 && (
                                            <Button
                                                variant="secondary"
                                                onClick={() => setFormStep((formStep - 1) as 1 | 2)}
                                                leftIcon={<ChevronLeft size={16} />}
                                                size="sm"
                                            >
                                                Back
                                            </Button>
                                        )}

                                        {formStep < 3 ? (
                                            <Button
                                                onClick={handleNextStep}
                                                rightIcon={<ArrowRight size={16} />}
                                                size="sm"
                                            >
                                                {formStep === 1 ? 'Next: Permissions' : 'Next: Branches'}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={handleSubmit}
                                                isLoading={createStaffMutation.isPending || updateStaffMutation.isPending}
                                                disabled={!form.firstName || !form.email || (!editingStaff && !form.password) || form.roleIds.length === 0}
                                                leftIcon={editingStaff ? <Pencil size={16} /> : <Rocket size={16} />}
                                                size="sm"
                                            >
                                                {editingStaff ? 'Update Staff Member' : 'Create Staff Member'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* CONFIRMATION MODAL */}
                <AnimatePresence>
                    {confirmAction && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmAction(null)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${confirmAction.type === 'delete' ? 'bg-rose-100' : confirmAction.type === 'deactivate' ? 'bg-amber-100' : 'bg-emerald-100'}`}>{confirmAction.type === 'delete' ? <Trash2 size={28} className="text-rose-500" /> : confirmAction.type === 'deactivate' ? <Ban size={28} className="text-amber-500" /> : <CheckCircle2 size={28} className="text-emerald-500" />}</div>
                                <h3 className="text-xl font-bold text-slate-900">{confirmAction.type === 'delete' ? 'Remove?' : confirmAction.type === 'deactivate' ? 'Deactivate?' : 'Activate?'}</h3>
                                <p className="text-sm text-slate-500 mt-2">{confirmAction.staff.firstName} {confirmAction.staff.lastName} will be {confirmAction.type === 'delete' ? 'deactivated.' : confirmAction.type === 'deactivate' ? 'unable to access the system.' : 'able to access the system again.'}</p>
                                <div className="flex gap-3 mt-8"><Button variant="secondary" onClick={() => setConfirmAction(null)} className="flex-1">Cancel</Button><Button variant={confirmAction.type === 'delete' ? 'danger' : confirmAction.type === 'deactivate' ? 'warning' : 'success'} onClick={() => { if (confirmAction.type === 'delete') deleteStaffMutation.mutate(confirmAction.staff.id); else toggleStatusMutation.mutate({ staffId: confirmAction.staff.id, isActive: confirmAction.type === 'activate' }); }} isLoading={deleteStaffMutation.isPending || toggleStatusMutation.isPending} className="flex-1">Confirm</Button></div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* RESET PIN MODAL */}
                <AnimatePresence>
                    {pinModal.open && pinModal.staff && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setPinModal(p => ({ ...p, open: false }))}>
                            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                                <div className="px-8 py-5 bg-gradient-to-r from-amber-500 to-orange-600 text-white flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20">
                                            <KeyRound size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-white">Reset Staff PIN</h2>
                                            <p className="text-amber-100 text-xs">{pinModal.staff.firstName} {pinModal.staff.lastName}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setPinModal(p => ({ ...p, open: false }))} className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30"><X size={18} /></button>
                                </div>
                                <div className="p-8 space-y-4">
                                    <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
                                        <KeyRound size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800 leading-relaxed">
                                            A 4 to 6 digit numeric PIN allows staff (supervisors, cashiers, managers) to authenticate on POS terminals, approve discounts, and authorize voids.
                                        </p>
                                    </div>

                                    {pinModal.error && (
                                        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-600 font-medium">
                                            {pinModal.error}
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">New PIN (4-6 digits)</label>
                                            <div className="relative">
                                                <input
                                                    type={pinModal.showPin ? 'text' : 'password'}
                                                    maxLength={6}
                                                    placeholder="Enter 4 to 6 digit PIN"
                                                    value={pinModal.pin}
                                                    onChange={e => {
                                                        const val = e.target.value.replace(/\D/g, '');
                                                        setPinModal(p => ({ ...p, pin: val, error: '' }));
                                                    }}
                                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono tracking-widest focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setPinModal(p => ({ ...p, showPin: !p.showPin }))}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400 hover:text-slate-600">
                                                    {pinModal.showPin ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Confirm PIN</label>
                                            <input
                                                type={pinModal.showPin ? 'text' : 'password'}
                                                maxLength={6}
                                                placeholder="Re-enter PIN"
                                                value={pinModal.confirmPin}
                                                onChange={e => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setPinModal(p => ({ ...p, confirmPin: val, error: '' }));
                                                }}
                                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono tracking-widest focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const gen = Math.floor(1000 + Math.random() * 9000).toString();
                                                setPinModal(p => ({ ...p, pin: gen, confirmPin: gen, showPin: true, error: '' }));
                                            }}
                                            className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5">
                                            <Sparkles size={14} className="text-amber-500" /> Generate Random 4-Digit PIN
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-slate-50 border-t border-slate-200 px-8 py-4 flex justify-end gap-3">
                                    <Button variant="secondary" onClick={() => setPinModal(p => ({ ...p, open: false }))}>Cancel</Button>
                                    <Button
                                        onClick={handleResetPinSubmit}
                                        isLoading={pinModal.isSubmitting}
                                        disabled={!pinModal.pin || pinModal.pin.length < 4 || pinModal.pin !== pinModal.confirmPin}
                                        className="!bg-gradient-to-r !from-amber-500 !to-orange-600 !text-white"
                                        leftIcon={<KeyRound size={16} />}>
                                        Save New PIN
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* MANAGE ROLES MODAL */}
                <AnimatePresence>
                    {roleModal.open && roleModal.staff && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setRoleModal(p => ({ ...p, open: false }))}>
                            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                                <div className="px-8 py-5 bg-gradient-to-r from-teal-600 to-emerald-700 text-white flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20">
                                            <Shield size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-white">Assign Staff Roles</h2>
                                            <p className="text-teal-100 text-xs">{roleModal.staff.firstName} {roleModal.staff.lastName}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setRoleModal(p => ({ ...p, open: false }))} className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30"><X size={18} /></button>
                                </div>
                                <div className="p-8 space-y-4">
                                    <p className="text-xs text-slate-500">
                                        Select the roles to grant or revoke for this staff member. At least one role is required.
                                    </p>
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {(rolesData || []).map((r: any) => {
                                            const isSelected = roleModal.roleIds.includes(r.id);
                                            return (
                                                <button
                                                    key={r.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setRoleModal(p => ({
                                                            ...p,
                                                            roleIds: isSelected
                                                                ? p.roleIds.filter(id => id !== r.id)
                                                                : [...p.roleIds, r.id],
                                                        }));
                                                    }}
                                                    className={clsx(
                                                        'px-3.5 py-2 rounded-xl text-xs font-semibold border-2 transition-all flex items-center gap-1.5',
                                                        isSelected
                                                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm'
                                                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                                    )}>
                                                    <Shield size={12} className={isSelected ? 'text-emerald-600' : 'text-slate-400'} />
                                                    {r.name}
                                                    {isSelected && <CheckCircle2 size={12} className="text-emerald-600 ml-1" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="bg-slate-50 border-t border-slate-200 px-8 py-4 flex justify-end gap-3">
                                    <Button variant="secondary" onClick={() => setRoleModal(p => ({ ...p, open: false }))}>Cancel</Button>
                                    <Button
                                        onClick={handleSaveRoles}
                                        isLoading={roleModal.isSubmitting}
                                        disabled={roleModal.roleIds.length === 0}
                                        leftIcon={<Save size={16} />}>
                                        Save Roles
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}